// src/pages/AboutUsPage.jsx - Enhanced Black & White Design
import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';
import { Target, BookOpen, Trophy, ChevronRight } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';

const AboutUsPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();

  // Scroll animations for each chapter
  const [heroRef, heroVisible] = useScrollAnimation();
  const [chapter1Ref, chapter1Visible] = useScrollAnimation();
  const [chapter2Ref, chapter2Visible] = useScrollAnimation();
  const [chapter3Ref, chapter3Visible] = useScrollAnimation();
  const [founderRef, founderVisible] = useScrollAnimation();
  const [chapter4Ref, chapter4Visible] = useScrollAnimation();
  const [missionRef, missionVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION - More Compact */}
      <section
        ref={heroRef}
        className={`
          relative py-16 sm:py-20 flex items-center justify-center overflow-hidden bg-white
          transition-all duration-1000
          ${heroVisible ? 'opacity-100' : 'opacity-0'}
        `}
      >
        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className={`transition-all duration-1000 delay-200 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-block px-5 py-1.5 bg-black text-white font-bold text-xs tracking-widest uppercase rounded-full mb-6 animate-pulse">
              {t('about.hero.title')}
            </div>

            {/* Title */}
            <h1 className="font-space text-4xl sm:text-5xl md:text-6xl font-black text-black mb-4 tracking-tight leading-none">
              Our Story
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-black/70 max-w-2xl mx-auto leading-relaxed">
              {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* CHAPTER 1 - Compact */}
      <section
        ref={chapter1Ref}
        className={`
          relative py-16 bg-gradient-to-br from-gray-50 to-white overflow-hidden
          transition-all duration-1000
          ${chapter1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {t('about.chapter1.number')}
              </span>
            </div>
            <h2 className="font-space text-3xl md:text-4xl font-black text-black mb-2 tracking-tight">
              {t('about.chapter1.title')}
            </h2>
            {t('about.chapter1.year') && (
              <p className="text-black/60 font-semibold">
                {t('about.chapter1.year')}
              </p>
            )}
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-black/70 leading-relaxed mb-6">
              {t('about.chapter1.text')}
            </p>

            {/* Quote - Black & White */}
            <div className="relative p-6 bg-black text-white rounded-xl border-[3px] border-black overflow-hidden group">
              <div className="absolute top-4 left-4 text-4xl font-black text-white/20">"</div>
              <p className="text-lg font-bold italic leading-relaxed relative z-10">
                {t('about.chapter1.quote')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent" />

      {/* CHAPTER 2 - Compact */}
      <section
        ref={chapter2Ref}
        className={`
          relative py-16 bg-white overflow-hidden
          transition-all duration-1000
          ${chapter2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {t('about.chapter2.number')}
              </span>
            </div>
            <h2 className="font-space text-3xl md:text-4xl font-black text-black tracking-tight">
              {t('about.chapter2.title')}
            </h2>
          </div>

          <p className="text-black/70 leading-relaxed text-lg">
            {t('about.chapter2.text')}
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent" />

      {/* CHAPTER 3 - Compact */}
      <section
        ref={chapter3Ref}
        className={`
          relative py-16 bg-gradient-to-br from-gray-50 to-white overflow-hidden
          transition-all duration-1000
          ${chapter3Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {t('about.chapter3.number')}
              </span>
            </div>
            <h2 className="font-space text-3xl md:text-4xl font-black text-black tracking-tight">
              {t('about.chapter3.title')}
            </h2>
          </div>

          <div className="space-y-6">
            <p className="text-black/70 leading-relaxed text-lg">
              {t('about.chapter3.text')}
            </p>

            {/* Highlight Box */}
            <div className="relative p-6 bg-white border-[3px] border-black rounded-xl shadow-xl">
              <p className="text-lg font-bold text-black leading-relaxed">
                {t('about.chapter3.highlight')}
              </p>
              <div className="absolute -top-2 -left-2 w-4 h-4 bg-black rounded-full" />
              <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-black rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent" />

      {/* FOUNDER SECTION - Compact */}
      <section
        ref={founderRef}
        className={`
          relative py-16 bg-white overflow-hidden
          transition-all duration-1000
          ${founderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {t('about.founder.number')}
              </span>
            </div>
            <h2 className="font-space text-3xl md:text-4xl font-black text-black tracking-tight">
              {t('about.founder.title')}
            </h2>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
            {/* Photo */}
            <div className="lg:col-span-2 flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-3 bg-black rounded-2xl opacity-5 group-hover:opacity-10 transition-opacity" />

                <div className="relative overflow-hidden rounded-2xl border-4 border-black shadow-2xl">
                  <img
                    src="/images/amir-safari.jpg"
                    alt="Emir Safari - Founder of Logami"
                    className="w-72 h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-72 h-80 bg-gradient-to-br from-black to-gray-600 items-center justify-center text-white font-black text-5xl">
                    AS
                  </div>
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>

                <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-black rounded-full animate-pulse" />
              </div>
            </div>

            {/* Bio */}
            <div className="lg:col-span-3 space-y-4">
              {t('about.founder.role') && (
                <div>
                  <h3 className="text-xl font-bold text-black mb-1">
                    {t('about.founder.role')}
                  </h3>
                  {t('about.founder.credentials') && (
                    <p className="text-black/60 font-semibold">
                      {t('about.founder.credentials')}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {t('about.founder.bio1') && (
                  <p className="text-black/70 leading-relaxed">
                    {t('about.founder.bio1')}
                  </p>
                )}
                {t('about.founder.bio2') && (
                  <p className="text-black/70 leading-relaxed">
                    {t('about.founder.bio2')}
                  </p>
                )}
                {t('about.founder.bio3') && (
                  <p className="text-black/70 leading-relaxed">
                    {t('about.founder.bio3')}
                  </p>
                )}
              </div>

              {t('about.founder.quote') && (
                <div className="bg-black text-white p-5 rounded-xl border-l-4 border-white/30">
                  <p className="italic leading-relaxed">
                    "{t('about.founder.quote')}"
                  </p>
                  <p className="mt-2 font-semibold">, Emir Safari</p>
                </div>
              )}

              <a
                href="/emir"
                className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-xl border-2 border-black bg-white font-space font-bold text-black hover:bg-black hover:text-white active:scale-[0.98] transition-all duration-300"
              >
                {t('about.founder.portfolioLink')}
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent" />

      {/* CHAPTER 4 - Compact */}
      <section
        ref={chapter4Ref}
        className={`
          relative py-16 bg-gradient-to-br from-gray-50 to-white overflow-hidden
          transition-all duration-1000
          ${chapter4Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {t('about.chapter4.number')}
              </span>
            </div>
            <h2 className="font-space text-3xl md:text-4xl font-black text-black tracking-tight">
              {t('about.chapter4.title')}
            </h2>
          </div>

          <p className="text-black/70 leading-relaxed text-lg">
            {t('about.chapter4.text')}
          </p>
        </div>
      </section>

      {/* MISSION SECTION - Compact */}
      <section
        ref={missionRef}
        className={`
          relative py-16 bg-white overflow-hidden
          transition-all duration-1000
          ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
        `}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-space text-3xl md:text-4xl font-black text-black mb-4 tracking-tight">
              What We Do
            </h2>
            <p className="text-lg text-black/70 max-w-2xl mx-auto">
              {t('about.chapter5.text')}
            </p>
          </div>

          {/* Mission Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: t('about.mission.plan.title'), text: t('about.mission.plan.text'), delay: '0s' },
              { icon: BookOpen, title: t('about.mission.practice.title'), text: t('about.mission.practice.text'), delay: '0.1s' },
              { icon: Trophy, title: t('about.mission.pass.title'), text: t('about.mission.pass.text'), delay: '0.2s' }
            ].map((item, index) => (
              <div
                key={index}
                className={`
                  group relative bg-white rounded-xl p-6 border-2 border-black/10 cursor-pointer overflow-hidden
                  transition-all duration-500 hover:border-black hover:shadow-2xl hover:scale-105
                  ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: missionVisible ? item.delay : '0s' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

                <div className="relative w-14 h-14 bg-black rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <item.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="font-space text-xl font-black text-black mb-3 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-black/70 leading-relaxed text-sm">
                  {item.text}
                </p>

                <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-black/10 rounded-br-xl group-hover:border-black transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA - Compact */}
      <section className="relative py-16 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative bg-white rounded-2xl p-10 sm:p-12 border-[3px] border-black overflow-hidden">
            <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

            <h2 className="font-space text-3xl sm:text-4xl font-black text-black mb-4 tracking-tight">
              {t('about.cta.title')}
            </h2>
            <p className="text-lg text-black/70 mb-8 max-w-xl mx-auto">
              {t('about.cta.description')}
            </p>

            <button
              onClick={() => navigate('/auth')}
              className="group/btn relative px-10 py-4 bg-black text-white font-space font-black text-lg rounded-xl border-3 border-black overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s infinite'
              }} />

              <span className="relative z-10 flex items-center justify-center gap-3">
                <span>{t('about.cta.button')}</span>
                <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover/btn:translate-x-2" />
              </span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;